import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setDone(true);
      toast.success("If the email exists, a reset link has been sent.");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="w-full max-w-md">
        <Logo size={40} className="mb-8" />
        <h1 className="text-3xl font-display font-bold tracking-tight">Reset your password</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Enter your email and we’ll send you a reset link.
        </p>
        <Card className="mt-8 p-6 border-border bg-card">
          {done ? (
            <div className="text-center py-4" data-testid="forgot-success">
              <p className="text-sm">
                If <span className="font-medium">{email}</span> is registered, you’ll receive a
                reset link shortly. (In demo mode, check the server logs.)
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              {err && (
                <Alert variant="destructive"><AlertDescription>{err}</AlertDescription></Alert>
              )}
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5"
                  data-testid="forgot-email-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-primary hover:bg-brand-blue-hover h-11"
                data-testid="forgot-submit-btn"
              >
                {loading && <Loader2 className="animate-spin mr-2" size={16} />}
                Send reset link
              </Button>
            </form>
          )}
        </Card>
        <Link to="/login" className="mt-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="forgot-back-link">
          <ArrowLeft size={14} /> Back to sign in
        </Link>
      </div>
    </div>
  );
}
